import { useState, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { ArrowRight, Upload, X, FileText, ShieldCheck, ExternalLink, ChevronDown, Play, Check } from 'lucide-react';
import { ACCEPT_ATTRIBUTE as ACCEPTED_FILE_TYPES } from '@optidev/shared-types';
import { validateFilesForUpload } from '@/lib/files/image-validation';
import toast from 'react-hot-toast';
import { getActiveBridge, CHILD_ACTION } from './OptiSignsBridge';
import { syncEmbedUserWithBackend } from './useOptiSignsBridge';
import { setPendingFiles } from './pending-files';
import { fetchEmbedVisitor, trackEmbedVisit } from './embed-visitors-api';
import { useEmbedStore } from '@/stores/embed-store';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { useEmbedSubscriptionGate } from './useEmbedSubscriptionGate';
import { EmbedSubscriptionBlockedModal } from './EmbedSubscriptionBlockedModal';
import { TransformationShowcase } from './TransformationShowcase';
import { MeetOptiDevModal, HowItWorksSection } from './MeetOptiDevModal';
import { EmbedDemoModal } from './EmbedDemoModal';
import { EmbedExamplesSection } from './EmbedExamplesSection';
import { getEmbedIcon } from './industry-icons';
import { useEmbedContent } from '@/hooks/useEmbedContent';
import { cn } from '@/lib/utils';
import { analytics } from '@/lib/analytics';
import { EMBED_EVENTS } from '@/lib/analytics/embed-events';
import { posthog } from '@/lib/analytics/posthog';
import type { EmbedDemoConfig } from '@/lib/api/features';

// A featured example for the Meet modal + "Three steps": a screenshot paired
// with the prompt that produced it (so the prompt step matches the thumbnail).
type FeaturedAsset = { thumbnail: string; prompt: string };

// Default pool (real deployed signage apps) used when the industry has no demos
// with thumbnails — unknown industry, or industries with idea-only demos.
const DEFAULT_ASSETS: FeaturedAsset[] = [
  {
    thumbnail: 'https://menu-board-56637.fly-app.optidev.ai/thumbnail.png',
    prompt: 'Build a digital menu board for our restaurant with items, prices, and a daily special',
  },
  {
    thumbnail: 'https://sales-dashboard-75882.fly-app.optidev.ai/thumbnail.png',
    prompt: "Build a sales leaderboard for our store managers, ranked by today's sales",
  },
  {
    thumbnail: 'https://days-since-incident-85971.fly-app.optidev.ai/thumbnail.png',
    prompt: 'Build a "Days Since Last Incident" safety counter for our shop floor screen',
  },
  {
    thumbnail: 'https://dock-schedule-97054.fly-app.optidev.ai/thumbnail.png',
    prompt: 'Build a warehouse dock schedule board showing inbound and outbound shipments',
  },
];

/** A demo's thumbnail: explicit `thumbnailUrl`, else the deploy's auto-captured
 *  `/thumbnail.png`, else none (idea-only demo). */
function demoThumbnail(demo: EmbedDemoConfig): string | undefined {
  if (demo.thumbnailUrl) return demo.thumbnailUrl;
  if (demo.deployUrl) return `${demo.deployUrl.replace(/\/$/, '')}/thumbnail.png`;
  return undefined;
}

/** A short, typed-prompt-friendly version of a demo's prompt (its first
 *  sentence), so the Idea step reads cleanly and fits the card. */
function demoPrompt(demo: EmbedDemoConfig): string {
  const firstLine = (demo.promptSeed ?? '').split('\n')[0].trim();
  const periodIdx = firstLine.indexOf('. ');
  const sentence =
    periodIdx > 0 ? firstLine.slice(0, periodIdx + 1) : firstLine.replace(/[:\s]+$/, '');
  return sentence || demo.label;
}

function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

// Endpoint-screen names shown in the "publish" step (Meet modal + Three steps) —
// the physical spots a display lands in, per industry. Falls back to a generic
// trio when the industry is unknown.
const SCREEN_NAMES: Record<string, [string, string, string]> = {
  manufacturing: ['Shop Floor', 'Break Room', 'Warehouse'],
  restaurant: ['Dining Room', 'Bar', 'Entrance'],
  retail: ['Storefront', 'Checkout', 'Entrance'],
  logistics: ['Dock Office', 'Shipping', 'Break Room'],
  healthcare: ['Waiting Room', 'Reception', 'Lobby'],
  hospitality: ['Lobby', 'Front Desk', 'Concierge'],
  gym_fitness: ['Lobby', 'Studio', 'Locker Room'],
  education: ['Main Hall', 'Cafeteria', 'Front Office'],
  religious_organization_and_nonprofits: ['Lobby', 'Sanctuary', 'Fellowship Hall'],
};
const DEFAULT_SCREEN_NAMES: [string, string, string] = ['Lobby', 'Reception', 'Break Room'];

// Grid + aurora backdrop, shared by the hero and the "How it works" section so
// they read as the same surface, with the tinted "Try a live example" panel
// sandwiched between them for sectioning rhythm.
const AURORA_BG: React.CSSProperties = {
  backgroundImage: [
    // Faint grid texture — uses currentColor so it adapts to light/dark theme
    'linear-gradient(color-mix(in srgb, currentColor 5%, transparent) 1px, transparent 1px)',
    'linear-gradient(90deg, color-mix(in srgb, currentColor 5%, transparent) 1px, transparent 1px)',
    // Soft color splashes — subtle aurora
    'radial-gradient(800px circle at 12% 18%, rgba(168, 85, 247, 0.22), transparent 60%)',
    'radial-gradient(700px circle at 88% 55%, rgba(99, 102, 241, 0.18), transparent 60%)',
    'radial-gradient(650px circle at 50% 120%, rgba(236, 72, 153, 0.14), transparent 60%)',
  ].join(', '),
  backgroundSize: '48px 48px, 48px 48px, auto, auto, auto',
};

interface ProvisionResponse {
  ok: boolean;
  workspaceId?: string;
  error?: string;
}

// Emerald 4-point sparkle accent — used decoratively around "Free" in the
// trial-confirm modal. The path uses cubic curves between the four tips
// so the points taper rather than meeting at sharp diamond corners.
function SparkleAccent({
  size,
  style,
}: {
  size: number;
  style: React.CSSProperties;
}) {
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute text-emerald-600 dark:text-emerald-400"
      style={{
        width: size,
        height: size,
        opacity: 0,
        animation: 'trialFreeSparkle 700ms cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
        ...style,
      }}
    >
      <svg viewBox="-50 -50 100 100" width={size} height={size}>
        <path
          d="M 0 -50 C 4 -16, 16 -4, 50 0 C 16 4, 4 16, 0 50 C -4 16, -16 4, -50 0 C -16 -4, -4 -16, 0 -50 Z"
          fill="currentColor"
        />
      </svg>
    </span>
  );
}

/**
 * Onboarding shown inside the OptiDev iframe when no workspace is linked yet.
 *
 * Flow:
 * 1. User types a prompt, optionally attaches files, clicks the arrow button
 * 2. sendRequest('PROVISION_WORKSPACE') → scio3 calls OptiPrime provision API
 * 3. On success: stash files in pending-files, update workspace store, set prompt in URL
 * 4. Clear needsProvisioning → ProtectedRoute renders DashboardLayout → Dashboard reads prompt + files
 */
export default function EmbedOnboarding() {
  const { isBlocked: isEmbedBlocked } = useEmbedSubscriptionGate();
  const needsAdminAccess = useEmbedStore((s) => s.needsAdminAccess);
  // Set by the bridge ON_INIT — gates the embed-visitors lead-tracking calls.
  const parentToken = useEmbedStore((s) => s.parentToken);
  // Industry classification from scio3 (may arrive after mount). Drives the
  // personalized chips, hero copy, live demos, and featured Meet-modal demo.
  // Onboarding survey use-cases are the fallback when industry is absent/unmatched.
  const industry = useEmbedStore((s) => s.industry);
  const onboarding = useEmbedStore((s) => s.onboarding);
  const { content } = useEmbedContent(industry, onboarding);

  // Featured-asset pool for the Meet modal + "Three steps": this industry's
  // demos (screenshot + matching prompt), falling back to the default pool when
  // none have thumbnails.
  const assetPool = useMemo<FeaturedAsset[]>(() => {
    const pool = content.demos
      .map((d) => {
        const thumbnail = demoThumbnail(d);
        return thumbnail ? { thumbnail, prompt: demoPrompt(d) } : null;
      })
      .filter((a): a is FeaturedAsset => a !== null);
    return pool.length > 0 ? pool : DEFAULT_ASSETS;
  }, [content.demos]);
  const [featuredAsset, setFeaturedAsset] = useState<FeaturedAsset>(() =>
    pickRandom(DEFAULT_ASSETS),
  );
  // Sync to the resolved industry pool once it loads (and on industry change).
  useEffect(() => {
    setFeaturedAsset(pickRandom(assetPool));
  }, [assetPool]);
  const rerollAsset = useCallback(() => {
    setFeaturedAsset(pickRandom(assetPool));
  }, [assetPool]);

  // Endpoint-screen names for the "publish" step, by industry.
  const screenNames = SCREEN_NAMES[content.industryKey] ?? DEFAULT_SCREEN_NAMES;

  const [prompt, setPrompt] = useState('');
  const [selectedDemo, setSelectedDemo] = useState<EmbedDemoConfig | null>(null);
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [showTrialConfirm, setShowTrialConfirm] = useState(false);
  const [showAdminRequest, setShowAdminRequest] = useState(false);
  const [error, setError] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [showMeetModal, setShowMeetModal] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // One-shot guard for embed_prompt_started — fires only on the first prompt
  // interaction (typing or chip click) per page load.
  const promptStartedFiredRef = useRef(false);
  // How the most recent modal open was triggered. The auto-open was removed
  // (welcome-modal-v3 concluded in favor of no-modal), so the modal now only
  // opens manually via the "Watch demo" button.
  const openSourceRef = useRef<'auto' | 'manual'>('manual');
  // Timestamp of the most recent modal open, used to compute dwell_ms on close.
  const modalOpenTimeRef = useRef<number>(0);

  // Fire onboarding-viewed once when the page mounts. Top of funnel.
  useEffect(() => {
    analytics.track(EMBED_EVENTS.ONBOARDING_VIEWED);
  }, []);

  // Server-side lead tracking (/api/embed-visitors): record the visit, and
  // restore the prompt from their last "Build it" click on a previous visit
  // (any device). These returning visitors already showed intent — greet them
  // with their idea instead of an empty box. Runs once the parent token
  // resolves; never overwrites text the visitor already typed this page load.
  const visitTrackedRef = useRef(false);
  // History length from the fetched record (+ this session's build clicks) —
  // reported on the trial-declined analytics event.
  const promptAttemptsRef = useRef(0);
  useEffect(() => {
    if (!parentToken || visitTrackedRef.current) return;
    visitTrackedRef.current = true;
    trackEmbedVisit({ event: 'visit' });

    void fetchEmbedVisitor().then((visitor) => {
      if (!visitor) return;
      promptAttemptsRef.current = visitor.promptHistory.length;
      const saved = visitor.draft.trim();
      if (!saved || visitor.status === 'converted' || promptStartedFiredRef.current) return;
      setPrompt(visitor.draft);
      // Mark prompt_started (source: restored) so the funnel stays monotonic
      // even when the visitor builds straight from the restored prompt.
      promptStartedFiredRef.current = true;
      analytics.track(EMBED_EVENTS.PROMPT_STARTED, { source: 'restored' });
      analytics.track(EMBED_EVENTS.PROMPT_RESTORED, {
        prompt_length: visitor.draft.length,
        saved_prompts_count: visitor.promptHistory.length,
        draft_age_ms: Date.now() - new Date(visitor.updatedAt).getTime(),
        visit_count: visitor.visitCount,
      });
    });
  }, [parentToken]);

  // Rotating example placeholder — inspires ideas without pre-filling the box.
  // The box stays empty (users type their own idea, so embed_prompt_started
  // fires only on real typing), while the placeholder slowly cycles through
  // several industry-relevant example prompts. Cycling naturally absorbs the
  // industry config arriving after mount (no jarring default→industry swap of
  // injected text) and surfaces more ideas than one fixed starter would.
  const promptExamples = useMemo<string[]>(() => {
    const seen = new Set<string>();
    const list: string[] = [];
    for (const raw of [
      ...content.demos.map(demoPrompt),
      ...content.chips.map((c) => c.prompt),
    ]) {
      const t = (raw ?? '').trim();
      if (t && !seen.has(t)) {
        seen.add(t);
        list.push(t);
      }
      if (list.length >= 6) break;
    }
    return list.length ? list : ['Describe the screen or app you want to build…'];
  }, [content.demos, content.chips]);

  const [promptFocused, setPromptFocused] = useState(false);
  const [typedPlaceholder, setTypedPlaceholder] = useState('');

  // Typewriter placeholder: type each example out character-by-character, hold,
  // clear, then move to the next and repeat — a cycling animated hint that
  // models the "describe your idea" action without pre-filling the box. Runs
  // only while the box is empty & unfocused. A self-scheduling timeout chain,
  // fully cancelled on cleanup, keeps it StrictMode-safe (the dev remount just
  // restarts it). Reduced-motion shows the first example statically.
  useEffect(() => {
    if (prompt !== '' || promptFocused || promptExamples.length === 0) return;

    const prefersReduced =
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    if (prefersReduced) {
      setTypedPlaceholder(promptExamples[0]);
      return;
    }

    const TYPE_MS = 26; // per character
    const HOLD_MS = 1800; // pause once an example is fully typed
    const GAP_MS = 450; // blank pause before the next example
    let idx = 0;
    let char = 0;
    let timer: ReturnType<typeof setTimeout>;

    const tick = () => {
      const example = promptExamples[idx % promptExamples.length];
      if (char <= example.length) {
        setTypedPlaceholder(example.slice(0, char));
        const doneTyping = char === example.length;
        char += 1;
        timer = setTimeout(tick, doneTyping ? HOLD_MS : TYPE_MS);
      } else {
        idx += 1;
        char = 0;
        setTypedPlaceholder('');
        timer = setTimeout(tick, GAP_MS);
      }
    };
    tick();
    return () => clearTimeout(timer);
  }, [prompt, promptFocused, promptExamples]);

  // Tag every event with which industry content the user actually saw, so the
  // funnel/experiment can be sliced by industry — including the no-industry case
  // (`default`), where we show a curated showcase mix. Re-registers if the
  // resolved industry changes (config loads after first paint).
  useEffect(() => {
    try {
      posthog.register({
        resolved_industry_key: content.industryKey,
        is_personalized: content.industryKey !== 'default',
        is_showcase_mix: content.industryKey === 'default' && content.demos.length > 0,
      });
    } catch {
      // analytics non-critical
    }
  }, [content.industryKey, content.demos.length]);

  const handleCloseMeetModal = useCallback(() => {
    setShowMeetModal(false);
  }, []);

  const handleOpenMeetModal = useCallback(() => {
    openSourceRef.current = 'manual';
    rerollAsset();
    setShowMeetModal(true);
  }, [rerollAsset]);

  const handleModalShown = useCallback((useCaseId: string) => {
    modalOpenTimeRef.current = Date.now();
    analytics.track(EMBED_EVENTS.DEMO_MODAL_SHOWN, {
      use_case_id: useCaseId,
      open_source: openSourceRef.current,
    });
  }, []);

  const handleModalCtaClick = useCallback((useCaseId: string, stepIndex: number) => {
    analytics.track(EMBED_EVENTS.DEMO_CTA_CLICKED, {
      use_case_id: useCaseId,
      dwell_ms: Date.now() - modalOpenTimeRef.current,
      dismissed_at_step_index: stepIndex,
      open_source: openSourceRef.current,
    });
    // "See more examples" CTA: the modal closes (onClose), then we scroll to the
    // live-examples section (or how-it-works when the industry has no live demos).
    const targetId = content.demos.length > 0 ? 'live-demos' : 'how-it-works';
    setTimeout(() => document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth' }), 0);
  }, [content.demos.length]);

  const handleModalDismiss = useCallback((useCaseId: string, stepIndex: number) => {
    analytics.track(EMBED_EVENTS.DEMO_MODAL_DISMISSED, {
      use_case_id: useCaseId,
      dwell_ms: Date.now() - modalOpenTimeRef.current,
      dismissed_at_step_index: stepIndex,
      open_source: openSourceRef.current,
    });
  }, []);

  const isImageFile = (file: File) => file.type.startsWith('image/');

  const addValidatedFiles = useCallback(async (rawFiles: File[]) => {
    const { valid, invalid } = await validateFilesForUpload(rawFiles);

    if (invalid.length > 0) {
      invalid.forEach(({ file, reason }) => {
        toast.error(`${file.name}: ${reason}`, { duration: 4000 });
      });
    }

    if (valid.length > 0) {
      setFiles(prev => [...prev, ...valid]);
    }
  }, []);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (!selected || selected.length === 0) return;
    await addValidatedFiles(Array.from(selected));
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [addValidatedFiles]);

  const handleRemoveFile = useCallback((index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleCreateClick = useCallback(() => {
    const trimmed = prompt.trim();
    if (!trimmed || isProvisioning) return;
    // Persist this committed prompt server-side (embed_visitors: draft +
    // prompt_history keep every "Build it" attempt across visits).
    trackEmbedVisit({ event: 'build_clicked', draft: trimmed, fileCount: files.length });
    promptAttemptsRef.current += 1;
    // Fire BUILD_CLICKED for everyone — measures intent regardless of admin status.
    const buildProps = {
      prompt_length: trimmed.length,
      has_files: files.length > 0,
      file_count: files.length,
    };
    // The prompt text itself goes to PostHog only (user-typed free text — keep
    // it out of GTM/ad platforms) so we can see what abandoners wanted to build.
    const promptTextProps = { prompt_text: trimmed.slice(0, 1000) };
    analytics.track(EMBED_EVENTS.BUILD_CLICKED, buildProps, {
      posthogOnlyData: promptTextProps,
    });
    if (needsAdminAccess) {
      // Non-admin path — fire ADMIN_BLOCK_SHOWN and route to the admin modal.
      analytics.track(EMBED_EVENTS.ADMIN_BLOCK_SHOWN, buildProps, {
        posthogOnlyData: promptTextProps,
      });
      setShowAdminRequest(true);
      return;
    }
    setShowTrialConfirm(true);
  }, [prompt, isProvisioning, needsAdminAccess, files.length]);

  const handleCloseAdminRequest = useCallback(() => {
    analytics.track(EMBED_EVENTS.ADMIN_BLOCK_DISMISSED);
    setShowAdminRequest(false);
  }, []);

  const handleConfirmTrial = useCallback(async () => {
    setShowTrialConfirm(false);
    const trimmed = prompt.trim();
    if (!trimmed || isProvisioning) return;

    setIsProvisioning(true);
    setError('');

    analytics.track(EMBED_EVENTS.TRIAL_CONFIRMED);

    try {
      const bridge = getActiveBridge();
      if (!bridge) throw new Error('Bridge not available');

      const response = await bridge.sendRequest<ProvisionResponse>(
        CHILD_ACTION.PROVISION_WORKSPACE,
      );

      if (!response.ok || !response.workspaceId) {
        throw new Error(response.error || 'Provisioning failed');
      }

      // Stash files for Dashboard to pick up and upload after mount
      if (files.length > 0) {
        setPendingFiles(files);
      }

      // Sync OptiDev user with the freshly-provisioned workspace BEFORE clearing
      // needsProvisioning. /api/workspaces/provision (called by OptiPrime as part
      // of the PROVISION_WORKSPACE flow) already created the User + WorkspaceMember
      // rows; this call returns that user so AuthContext can populate.
      //
      // Throw on failure so the catch block below keeps needsProvisioning=true
      // and shows the user a "try again" message. Otherwise we'd clear
      // needsProvisioning while AuthContext is still null, mounting Dashboard
      // in a half-broken state where workspace-selector etc. have no token.
      const synced = await syncEmbedUserWithBackend(response.workspaceId);
      if (!synced) {
        throw new Error('Failed to sync user with OptiDev backend');
      }

      // Update workspace store — also syncs ?ws= in URL via history.replaceState
      useWorkspaceStore.getState().setActiveWorkspaceId(response.workspaceId);

      analytics.track(EMBED_EVENTS.WORKSPACE_PROVISIONED, {
        workspace_id: response.workspaceId,
      });
      // No lead-tracking call needed here: provisionWorkspace marks the
      // embed_visitors row converted server-side.

      // Add prompt params directly via history API (not navigate()).
      // BrowserRouter uses v7_startTransition which defers navigate() updates,
      // so Dashboard would mount before the URL has the prompt params.
      // history.replaceState is synchronous — guaranteed visible on next mount.
      const url = new URL(window.location.href);
      url.searchParams.set('prompt', btoa(encodeURIComponent(trimmed)));
      url.searchParams.set('autoSubmit', 'true');
      window.history.replaceState({}, '', url.toString());

      // Clear provisioning flag — ProtectedRoute will now render DashboardLayout
      // Dashboard reads prompt from window.location.search on mount
      useEmbedStore.getState().setNeedsProvisioning(false);
    } catch (err) {
      console.error('[EmbedOnboarding] provision error:', err);
      setError('Something went wrong. Please try again.');
      setIsProvisioning(false);
    }
  }, [prompt, isProvisioning, files]);

  const handleCancelTrial = useCallback(() => {
    setShowTrialConfirm(false);
    const trimmed = prompt.trim();
    // The key abandonment moment: reached the trial modal, closed it without
    // confirming. Flips the lead's status to trial_declined (hottest segment
    // for re-engagement); keepalive lets it survive an immediate tab close.
    trackEmbedVisit({ event: 'trial_declined', draft: trimmed, fileCount: files.length });
    analytics.track(
      EMBED_EVENTS.TRIAL_DECLINED,
      {
        prompt_length: trimmed.length,
        has_files: files.length > 0,
        file_count: files.length,
        saved_prompts_count: promptAttemptsRef.current,
      },
      { posthogOnlyData: { prompt_text: trimmed.slice(0, 1000) } },
    );
  }, [prompt, files.length]);

  const handleChipClick = useCallback((chipPrompt: string, chipLabel: string) => {
    setPrompt(chipPrompt);
    textareaRef.current?.focus();
    if (!promptStartedFiredRef.current) {
      promptStartedFiredRef.current = true;
      analytics.track(EMBED_EVENTS.PROMPT_STARTED, {
        source: 'chip',
        chip_label: chipLabel,
      });
    }
  }, []);

  // Sends the visitor back up to the hero prompt box and focuses it (final CTA).
  const handleScrollToPrompt = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    textareaRef.current?.focus({ preventScroll: true });
  }, []);

  // Seeds the prompt box from a demo idea and scrolls back up to it. Used by
  // the demo modal's "Build my own" and by cards that have no live app.
  const handleUseDemoIdea = useCallback((seed: string) => {
    setPrompt(seed);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    textareaRef.current?.focus();
    if (!promptStartedFiredRef.current) {
      promptStartedFiredRef.current = true;
      analytics.track(EMBED_EVENTS.PROMPT_STARTED, { source: 'demo' });
    }
  }, []);

  // Open the live iframe preview (demos with a deployUrl).
  const handleTryLive = useCallback(
    (demo: EmbedDemoConfig) => {
      setSelectedDemo(demo);
      analytics.track(EMBED_EVENTS.DEMO_APP_OPENED, {
        industry_key: content.industryKey,
        demo_label: demo.label,
        deploy_url: demo.deployUrl,
      });
    },
    [content.industryKey],
  );

  // Seed the prompt from a demo's idea (cards without a live app, or "Build my own").
  const handleDemoUseIdea = useCallback(
    (demo: EmbedDemoConfig) => {
      if (demo.promptSeed) handleUseDemoIdea(demo.promptSeed);
    },
    [handleUseDemoIdea],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey && prompt.trim() && !isProvisioning) {
        e.preventDefault();
        handleCreateClick();
      }
    },
    [prompt, isProvisioning, handleCreateClick],
  );

  // Auto-grow the prompt textarea as the visitor types (or after a chip
  // populates it). Capped at 220px — beyond that the textarea scrolls
  // internally rather than pushing the page layout. Reset to 'auto'
  // first so scrollHeight reflects content, not the previously-set
  // height. useLayoutEffect avoids a one-frame flicker on fast typing.
  useLayoutEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 220)}px`;
  }, [prompt, files.length]);

  if (isProvisioning) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-purple-500" />
          <p className="text-sm text-muted-foreground">Setting up your workspace...</p>
        </div>
      </div>
    );
  }

  if (isEmbedBlocked) {
    return <EmbedSubscriptionBlockedModal />;
  }

  return (
    // No `overflow-y-auto` here: the page scrolls on the window (body scrolls;
    // no height-constrained ancestor). An inner overflow container would grow
    // instead of scrolling, becoming a dead scroll context that breaks the
    // `position: sticky` steps in the How-it-works section.
    <div className="relative min-h-screen bg-background dark:bg-[#101013]">

      {/* ─── HERO — full-viewport prompt zone (aurora backdrop) ─── */}
      <section
        className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-5 py-16"
        style={AURORA_BG}
      >
        <div className="mx-auto w-full max-w-3xl">
          <div className="text-center">
            {/* Hero copy stays generic — industry personalizes the chips, demo
                cards, and featured Meet-modal demo, not the headline. */}
            <h1 className="text-[2rem] font-semibold leading-[1.08] tracking-tight sm:text-[2.5rem] lg:text-5xl">
              Your screens.
              <br />
              <span className="text-muted-foreground">Anything you can describe.</span>
            </h1>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-[17px]">
              Describe what you want in plain words — attach a file or link if you'd like to.
              <br />
              OptiDev builds it, ready for any display.
            </p>

            {/* "Watch demo" — a breathing purple glow behind the pill draws
                the eye to it as the demo-discovery entry point. */}
            <div className="mt-3 flex justify-center">
              <div className="relative inline-flex">
                {/* Breathing purple aura — edge-pinned (inset) so the ONLY
                    transform is the animation's scale; translate-based centering
                    here would fight the scale keyframe and shove the glow off to
                    the corner. */}
                {/* Light mode: bigger + brighter, but in LIGHTER violet tones
                    (purple-400 core + purple-300 halo) so it reads as a soft
                    lilac glow rather than a dark purple smudge on white. */}
                <span
                  aria-hidden
                  className="pointer-events-none absolute -inset-8 rounded-full dark:hidden"
                  style={{
                    background:
                      'radial-gradient(closest-side, rgba(192,132,252,0.95), rgba(192,132,252,0) 58%), radial-gradient(closest-side, rgba(216,180,254,0.6), rgba(216,180,254,0) 84%)',
                    filter: 'blur(12px)',
                    animation: 'embedDemoBreathe 1.8s ease-in-out infinite',
                  }}
                />
                {/* Dark mode: unchanged. */}
                <span
                  aria-hidden
                  className="pointer-events-none absolute -inset-5 hidden rounded-full dark:block"
                  style={{
                    background:
                      'radial-gradient(closest-side, rgba(168,85,247,0.9), rgba(168,85,247,0.2) 65%, rgba(168,85,247,0))',
                    filter: 'blur(10px)',
                    animation: 'embedDemoBreathe 1.8s ease-in-out infinite',
                  }}
                />
                {/* Light mode: white pill with border + shadow, dark text —
                    leans on the glow for pop rather than a loud fill. Dark
                    mode: keep the light pill. Purple icon circle in both. */}
                <button
                  type="button"
                  onClick={handleOpenMeetModal}
                  className="relative inline-flex items-center gap-2 rounded-full border border-zinc-400/60 bg-white px-4 py-1.5 text-xs font-semibold text-zinc-900 shadow-md transition-transform hover:scale-[1.03] dark:border-transparent dark:bg-zinc-200"
                >
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-purple-600 text-white">
                    <Play className="h-2.5 w-2.5 fill-current" strokeWidth={0} />
                  </span>
                  Watch demo
                </button>
              </div>
              <style>{`
                @keyframes embedDemoBreathe {
                  0%, 100% { opacity: 0.5; transform: scale(0.9); }
                  50%      { opacity: 1; transform: scale(1.28); }
                }
                @keyframes embedCaretBlink {
                  0%, 100% { opacity: 1; }
                  50%      { opacity: 0; }
                }
              `}</style>
            </div>
          </div>

          {/* Prompt input — auto-grows from a comfortable base height. */}
          <div className="relative mt-7 rounded-2xl border border-border bg-card shadow-md transition-all focus-within:border-purple-500/40 focus-within:shadow-lg focus-within:ring-2 focus-within:ring-purple-500/15">
            {/* Typewriter example placeholder — types example ideas out, one
                after another, while the box is empty & unfocused. Sits behind
                the transparent textarea. */}
            {prompt === '' && !promptFocused && (
              <div
                aria-hidden
                className="pointer-events-none absolute left-5 right-5 top-5 text-[15px] leading-relaxed text-muted-foreground/55 sm:text-base"
              >
                {typedPlaceholder}
                <span
                  className="ml-0.5 inline-block h-[1em] w-px translate-y-[0.15em] bg-current opacity-60 motion-reduce:hidden"
                  style={{ animation: 'embedCaretBlink 1s step-end infinite' }}
                />
              </div>
            )}
            <textarea
              ref={textareaRef}
              value={prompt}
              onFocus={() => setPromptFocused(true)}
              onBlur={() => setPromptFocused(false)}
              onChange={(e) => {
                setPrompt(e.target.value);
                if (!promptStartedFiredRef.current && e.target.value.length > 0) {
                  promptStartedFiredRef.current = true;
                  analytics.track(EMBED_EVENTS.PROMPT_STARTED, { source: 'typing' });
                }
              }}
              onKeyDown={handleKeyDown}
              placeholder=""
              rows={3}
              className={cn(
                'relative w-full resize-none overflow-y-auto rounded-t-2xl bg-transparent px-5 pt-5 text-[15px] leading-relaxed text-foreground placeholder:text-muted-foreground/70 focus:outline-none sm:text-base',
                files.length > 0 ? 'pb-14' : 'pb-3',
              )}
              style={{ minHeight: '108px', maxHeight: '240px' }}
            />

            {/* File thumbnails */}
            {files.length > 0 && (
              <div className="absolute -mt-14 ml-4 flex gap-2 px-1">
                {files.map((file, index) => {
                  const isImage = isImageFile(file);
                  const previewUrl = isImage ? URL.createObjectURL(file) : '';
                  return (
                    <div key={`${file.name}-${index}`} className="group/thumb relative shrink-0">
                      <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-lg border border-border bg-muted">
                        {isImage && previewUrl ? (
                          <img src={previewUrl} alt={file.name} className="h-full w-full object-cover" />
                        ) : (
                          <FileText className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveFile(index)}
                        className="absolute -right-1.5 -top-1.5 z-10 flex h-5 w-5 items-center justify-center rounded-full border border-white/30 bg-gray-900/95 opacity-0 shadow-lg transition-all hover:bg-red-600 group-hover/thumb:opacity-100 dark:bg-gray-800/95"
                        title="Remove"
                      >
                        <X className="h-3 w-3 text-white" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Action bar — slimmer than the input zone above so the focus
                stays on the prompt itself, not the controls. */}
            <div className="flex items-center justify-between border-t border-border/40 px-3 py-2">
              <div className="flex items-center gap-2">
                {!needsAdminAccess && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    title="Attach files"
                  >
                    <Upload className="h-3.5 w-3.5" />
                    Attach
                  </button>
                )}
                {needsAdminAccess && (
                  <span className="flex items-center gap-1.5 px-2 text-xs text-muted-foreground/80">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    Admin approval required
                  </span>
                )}
              </div>

              <button
                onClick={handleCreateClick}
                disabled={!prompt.trim() || isProvisioning}
                aria-label="Create"
                className={cn(
                  'flex h-8 items-center gap-1.5 rounded-lg px-3 text-xs font-medium transition-all',
                  prompt.trim()
                    ? 'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90'
                    : 'cursor-not-allowed bg-muted text-muted-foreground',
                )}
              >
                Build it
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_FILE_TYPES}
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />

          {error && <p className="mt-3 text-center text-xs text-destructive">{error}</p>}
        </div>

        {/* Scroll cue — anchors to the first section below the hero (live
            demos when the industry has any, otherwise the how-it-works steps) */}
        <a
          href={content.demos.length > 0 ? '#live-demos' : '#how-it-works'}
          aria-label="See it in action"
          className="absolute bottom-6 left-1/2 flex -translate-x-1/2 flex-col items-center gap-1.5 text-foreground/85 transition-colors hover:text-foreground"
        >
          <span className="text-[11px] font-semibold uppercase tracking-[0.2em] dark:drop-shadow-[0_1px_8px_rgba(168,85,247,0.5)]">
            See it in action
          </span>
          <ChevronDown className="h-7 w-7 text-purple-600 animate-[meetScrollCue_1.5s_ease-in-out_infinite] dark:text-purple-400" strokeWidth={2.5} />
        </a>
      </section>

      {/* ─── LIVE DEMOS — industry-personalized "try a real app" cards ───
       *  Only rendered when the resolved industry (or default) has demos
       *  configured in app_configs. Each card opens a live iframe preview. */}
      {content.demos.length > 0 && (
        <section id="live-demos" className="bg-muted/20 dark:bg-black/10">
          {/* Header stays centered + constrained; its own vertical padding keeps
              the rhythm even with the example bands below (which go full-width). */}
          <div className="mx-auto flex max-w-[1280px] flex-col items-center px-5 py-24 text-center sm:py-32">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-purple-500/20 bg-purple-500/10 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em] text-purple-600 dark:text-purple-400">
              <span className="h-1.5 w-1.5 rounded-full bg-purple-500" />
              Real-life examples
            </span>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">
              See it running on real screens
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-base leading-relaxed text-muted-foreground">
              Open a live OptiDev app, or start from an idea — then make it your own in one click.
            </p>
          </div>
          <EmbedExamplesSection
            demos={content.demos}
            onTryLive={handleTryLive}
            onUseIdea={handleDemoUseIdea}
          />
        </section>
      )}

      {/* ─── HOW IT WORKS — shares the hero's aurora backdrop, with the tinted
          "Try a live example" panel sandwiched between them. Scroll-driven:
          a tall pinned track advances the 3 steps. No `overflow-hidden` here —
          it would break the inner `position: sticky` pinning. ─── */}
      <section
        id="how-it-works"
        className="relative px-5 pb-10 pt-12 sm:pb-12 sm:pt-16"
        style={AURORA_BG}
      >
        <div className="mx-auto max-w-[1280px]">
          <div className="mb-8 flex flex-col items-center text-center">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-purple-500/20 bg-purple-500/10 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em] text-purple-600 dark:text-purple-400">
              <span className="h-1.5 w-1.5 rounded-full bg-purple-500" />
              How it works
            </span>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">
              Three steps from idea to display
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-base leading-relaxed text-muted-foreground">
              From the moment you describe what you need to the screens lighting up — OptiDev
              handles the build.
            </p>
          </div>
          <HowItWorksSection
            thumbnailUrl={featuredAsset.thumbnail}
            promptText={featuredAsset.prompt}
            screenNames={screenNames}
          />
        </div>
      </section>

      {/* ─── FINAL CTA — sends the visitor back to the hero prompt box ─── */}
      <section
        className="relative overflow-hidden px-5 py-24 text-center sm:py-28"
        style={AURORA_BG}
      >
        <div className="mx-auto max-w-2xl">
          <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">
            Ready to build yours?
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-base leading-relaxed text-muted-foreground">
            Describe what you want on your screens — OptiDev builds it in minutes.
          </p>
          <button
            type="button"
            onClick={handleScrollToPrompt}
            className="mt-8 inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-base font-medium text-primary-foreground shadow-sm transition-all hover:-translate-y-0.5 hover:bg-primary/90 hover:shadow-lg"
          >
            Start building
          </button>
        </div>
      </section>

      {/* ─── TRANSFORMATION SHOWCASE — proof / before-after ───
       *  Temporarily hidden while we work on the rest of the page.
       *  Re-enable by removing the wrapping `false &&` guard. */}
      {false && (
        <section
          id="showcase"
          className="border-t border-border/40 bg-card/30 px-5 py-20 sm:py-24"
        >
          <div className="mx-auto max-w-[1280px]">
            <div className="mb-12 text-center">
              <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-purple-600/80 dark:text-purple-400/80">
                Real transformations
              </p>
              <h2 className="mt-2 text-3xl font-semibold tracking-tight sm:text-4xl">
                From messy data to polished displays
              </h2>
              <p className="mx-auto mt-3 max-w-2xl text-base leading-relaxed text-muted-foreground">
                Drop in what you have today — spreadsheets, websites, PDFs — and OptiDev rebuilds it
                for the screen.
              </p>
            </div>
            <TransformationShowcase />
          </div>
        </section>
      )}

      {/* Quiet footer strip */}
      <div className="border-t border-border/50">
        <div className="mx-auto flex max-w-[1480px] flex-wrap items-center justify-center gap-3 px-5 py-3 text-[11px] text-muted-foreground">
          <span>100 free credits</span>
          <span className="text-muted-foreground/30">·</span>
          <span>14-day free trial</span>
          <span className="text-muted-foreground/30">·</span>
          <span>Publish to any OptiSigns screen</span>
        </div>
      </div>

      {/* Soft bounce on the scroll cue chevron — quieter than animate-bounce.
          trialFreeStroke draws the emerald highlighter behind "Free" in the
          trial-confirm modal — same skewX paint-stroke pattern as
          MeetOptiDevModal's title brush, in emerald to signal "good thing". */}
      <style>{`
        @keyframes meetScrollCue {
          0%, 100% { transform: translateY(0); opacity: 0.65; }
          50%      { transform: translateY(9px); opacity: 1; }
        }
        @keyframes trialFreeSparkle {
          0%   { opacity: 0; transform: scale(0) rotate(-60deg); }
          60%  { opacity: 1; transform: scale(1.15) rotate(5deg); }
          100% { opacity: 1; transform: scale(1) rotate(0deg); }
        }
        @keyframes trialFreeShimmer {
          to { background-position: 0% 50%; }
        }
        /* Light mode — emerald (500) base with a brighter emerald (300) shimmer
           band. Bright enough to feel "alive" on the white card while still
           keeping enough contrast to be readable. The radial wash sibling span
           does most of the glow work; this drop-shadow is just a faint edge. */
        .trial-free-word {
          background-image: linear-gradient(
            105deg,
            rgb(16, 185, 129) 30%,
            rgb(110, 231, 183) 50%,
            rgb(16, 185, 129) 70%
          );
          filter:
            drop-shadow(0 0 8px rgba(16, 185, 129, 0.22))
            drop-shadow(0 0 2px rgba(16, 185, 129, 0.16));
        }
        /* Dark mode — brighter emerald (400) base with a near-mint band so the
           word and shimmer still pop on the dark card. */
        .dark .trial-free-word {
          background-image: linear-gradient(
            105deg,
            rgb(52, 211, 153) 30%,
            rgb(187, 247, 208) 50%,
            rgb(52, 211, 153) 70%
          );
          filter:
            drop-shadow(0 0 12px rgba(52, 211, 153, 0.22))
            drop-shadow(0 0 3px rgba(52, 211, 153, 0.15));
        }
      `}</style>

      {/* Meet OptiDev intro modal — shown once per browser, replayable from the footer link. */}
      <MeetOptiDevModal
        open={showMeetModal}
        onClose={handleCloseMeetModal}
        featuredUseCaseId={content.featuredUseCase}
        thumbnailUrl={featuredAsset.thumbnail}
        promptText={featuredAsset.prompt}
        screenNames={screenNames}
        onShown={handleModalShown}
        onCtaClick={handleModalCtaClick}
        onDismiss={handleModalDismiss}
      />

      {/* Live demo preview — opens a deployed OptiDev app in an iframe */}
      <EmbedDemoModal
        demo={selectedDemo}
        onClose={() => setSelectedDemo(null)}
      />

      {/* Admin access request modal — shown to non-admin embed users on submit */}
      {showAdminRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-xl">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-purple-500/10 text-purple-500">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold leading-tight">
                  Ask your admin for access
                </h3>
                <p className="mt-1.5 text-sm text-muted-foreground">
                  Reach out to your admin to get access to OptiDev
                </p>
              </div>
            </div>

            <div className="mt-5 rounded-xl border border-border bg-muted/40 p-3.5">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground/80">
                Want to try it right now?
              </p>
              <p className="mt-1.5 text-sm text-foreground/90">
                Sign up at{' '}
                <a
                  href="https://optidev.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-purple-600 hover:underline dark:text-purple-400"
                >
                  optidev.ai
                </a>{' '}
                to use OptiDev on your own — no admin needed.
              </p>
            </div>

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                onClick={handleCloseAdminRequest}
                className="rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted"
              >
                Got it
              </button>
              <a
                href="https://optidev.ai"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => analytics.track(EMBED_EVENTS.ADMIN_EXTERNAL_CLICKED)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-purple-700"
              >
                Try OptiDev
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Trial confirmation modal */}
      {showTrialConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 backdrop-blur-sm">
          <div className="relative w-full max-w-sm overflow-hidden rounded-2xl border border-border bg-card shadow-2xl">
            {/* Close */}
            <button
              type="button"
              onClick={handleCancelTrial}
              aria-label="Close"
              className="absolute right-3 top-3 z-10 flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>

            {/* Hero — "free" as the anchor, with an emerald paint-stroke
                animating in behind the word to signal "good thing".
                Commercial terms live as fine print below the CTA. */}
            <div className="px-6 pb-1 pt-8 text-center">
              <h3 className="text-[30px] font-semibold leading-[1.1] tracking-tight text-foreground">
                Try for{' '}
                <span
                  className="relative inline-block"
                  style={{ marginLeft: '0.04em' }}
                >
                  {/* Three emerald sparkles around "free", staggered in. */}
                  <SparkleAccent
                    size={11}
                    style={{ top: '-10px', left: '-6px', animationDelay: '200ms' }}
                  />
                  <SparkleAccent
                    size={15}
                    style={{ top: '-14px', right: '-6px', animationDelay: '380ms' }}
                  />
                  <SparkleAccent
                    size={9}
                    style={{ bottom: '-6px', right: '-12px', animationDelay: '560ms' }}
                  />
                  {/* The word "free" — emerald via background-clip: text, with
                      a brighter band baked into the gradient. Animating
                      background-position sweeps that band L→R across the
                      characters once on mount. Gradient + drop-shadow colors
                      live in CSS (trial-free-word class) so they can switch
                      between darker emerald-600 in light mode and brighter
                      emerald-400 in dark mode without losing contrast. */}
                  <span
                    className="trial-free-word relative inline-block"
                    style={{
                      zIndex: 1,
                      backgroundSize: '260% 100%',
                      backgroundPosition: '100% 50%',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      color: 'transparent',
                      animation:
                        'trialFreeShimmer 1100ms cubic-bezier(0.4, 0, 0.6, 1) 250ms forwards',
                    }}
                  >
                    free
                  </span>
                </span>
              </h3>
            </div>

            {/* Value bullets — what you get from the app. Emerald check tint
                and text both flow through theme tokens (light + dark). */}
            <ul className="mt-4 flex flex-col gap-2.5 px-7 text-left">
              {[
                'Chat with AI to build apps easily',
                'Push to 3 OptiSigns screens, free & secure',
                'No credit card required',
              ].map((point) => (
                <li
                  key={point}
                  className="flex items-start gap-2.5 text-sm text-foreground"
                >
                  <span className="mt-0.5 flex h-[17px] w-[17px] flex-none items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                    <Check className="h-2.5 w-2.5" strokeWidth={3.5} />
                  </span>
                  <span>{point}</span>
                </li>
              ))}
            </ul>

            {/* CTA + fine print — commercial terms sit below the button.
                Held at 13px so the $30 renewal stays legible (auto-renew
                disclosure); no bold + muted so it stays recessive. */}
            <div className="px-6 pb-3.5 pt-[30px]">
              <button
                type="button"
                onClick={handleConfirmTrial}
                className="w-full rounded-xl bg-purple-600 py-3 text-sm font-medium text-white shadow-sm transition-colors hover:bg-purple-700"
              >
                Create your app
              </button>
              <p className="mt-2 text-center text-[13px] leading-relaxed text-muted-foreground/70">
                14-day trial · then $30/month · cancel anytime
              </p>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
